import { apiRequest, buildUrl } from './client';
import {
  BillingSettings,
  BillingUserProjection,
  CompanyBillingStatus,
  CreateStandardLicenseBody,
  HostedPageResponse,
  InvoiceRecord,
  LicenseRecord,
  SubscriptionRecord,
  UpdateBillingSettingsBody,
  UUID,
} from './types';

export const billingApi = {
  // --------------------------------------------------------------------------
  // Client Billing Overview, Subscriptions & Licenses (`/ms-license/*`)
  // --------------------------------------------------------------------------
  getCompanyBillingStatus(companyId?: UUID): Promise<CompanyBillingStatus> {
    if (companyId) {
      return apiRequest<CompanyBillingStatus>(
        `/ms-license/billing/${companyId}/company-billing-status`,
      );
    }
    return apiRequest<CompanyBillingStatus>('/ms-license/billing/company-billing-status');
  },

  getMySubscriptions(companyId?: UUID): Promise<SubscriptionRecord[]> {
    return apiRequest<SubscriptionRecord[]>('/ms-license/billing/get-my-subscriptions', {
      query: companyId ? { company_id: companyId } : undefined,
    });
  },

  getMyInvoices(companyId?: UUID): Promise<InvoiceRecord[]> {
    return apiRequest<InvoiceRecord[]>('/ms-license/billing/get-my-invoices', {
      query: companyId ? { company_id: companyId } : undefined,
    });
  },

  getLicenses(companyId?: UUID): Promise<LicenseRecord[]> {
    return apiRequest<LicenseRecord[]>('/ms-license/get-licenses', {
      query: companyId ? { company_id: companyId } : undefined,
    });
  },

  createStandardLicense(body: CreateStandardLicenseBody): Promise<LicenseRecord> {
    return apiRequest<LicenseRecord>('/ms-license/create-standard-license', {
      method: 'POST',
      body,
    });
  },

  cancelSubscription(companyId: UUID, subscriptionId?: UUID): Promise<SubscriptionRecord> {
    return apiRequest<SubscriptionRecord>(
      `/ms-license/billing/${companyId}/cancel-subscription`,
      {
        method: 'POST',
        body: subscriptionId ? { subscription_id: subscriptionId } : {},
      },
    );
  },

  reactivateSubscription(companyId: UUID, subscriptionId?: UUID): Promise<SubscriptionRecord> {
    return apiRequest<SubscriptionRecord>(
      `/ms-license/billing/${companyId}/reactivate-subscription`,
      {
        method: 'POST',
        body: subscriptionId ? { subscription_id: subscriptionId } : {},
      },
    );
  },

  watchSubscription(
    companyId: UUID,
    email: string,
    subscriptionId?: UUID,
  ): Promise<SubscriptionRecord> {
    return apiRequest<SubscriptionRecord>(
      `/ms-license/billing/${companyId}/watch-subscription`,
      {
        method: 'POST',
        body: {
          email,
          subscription_id: subscriptionId,
        },
      },
    );
  },

  stopWatchSubscription(
    companyId: UUID,
    email: string,
    subscriptionId?: UUID,
  ): Promise<SubscriptionRecord> {
    return apiRequest<SubscriptionRecord>(
      `/ms-license/billing/${companyId}/watch-subscription-stop`,
      {
        method: 'POST',
        body: {
          email,
          subscription_id: subscriptionId,
        },
      },
    );
  },

  emailInvoice(
    invoiceId: UUID,
    emails: string[],
    companyId?: UUID,
  ): Promise<InvoiceRecord> {
    return apiRequest<InvoiceRecord>('/ms-license/billing/email-an-invoice', {
      method: 'POST',
      body: {
        company_id: companyId,
        invoice_id: invoiceId,
        emails,
      },
    });
  },

  // --------------------------------------------------------------------------
  // Billing Contacts & Primary Billing Receiver (PBR)
  // --------------------------------------------------------------------------
  listBillingUsers(companyId: UUID): Promise<BillingUserProjection[]> {
    return apiRequest<BillingUserProjection[]>(`/ms-license/billing/${companyId}/billing-users`);
  },

  addBillingUser(companyId: UUID, userId: UUID): Promise<BillingUserProjection> {
    return apiRequest<BillingUserProjection>(`/ms-license/billing/${companyId}/billing-users`, {
      method: 'POST',
      body: { user_id: userId },
    });
  },

  removeBillingUser(companyId: UUID, userId: UUID): Promise<{ removed: boolean }> {
    return apiRequest<{ removed: boolean }>(
      `/ms-license/billing/${companyId}/billing-users/${userId}`,
      { method: 'DELETE' },
    );
  },

  setPrimaryBillingUser(companyId: UUID, userId: UUID): Promise<BillingUserProjection> {
    return apiRequest<BillingUserProjection>(
      `/ms-license/billing/${companyId}/billing-users/${userId}/set-primary`,
      { method: 'POST' },
    );
  },

  // --------------------------------------------------------------------------
  // Hosted Payment / Card Management & Invoice PDF Helpers
  // --------------------------------------------------------------------------
  getNewSubscriptionHostedPage(
    planCode = 'SYNC-STD-MONTHLY',
    companyId?: UUID,
  ): Promise<HostedPageResponse> {
    return apiRequest<HostedPageResponse>(
      '/ms-license/billing/get-new-subscription-hosted-page',
      {
        query: {
          plan_code: planCode,
          company_id: companyId,
        },
      },
    );
  },

  getUpdateSubscriptionHostedPage(
    subscriptionId: string,
    planCode = 'SYNC-STD-MONTHLY',
    companyId?: UUID,
  ): Promise<HostedPageResponse> {
    return apiRequest<HostedPageResponse>(
      '/ms-license/billing/get-update-subscription-hosted-page',
      {
        query: {
          subscription_id: subscriptionId,
          plan_code: planCode,
          company_id: companyId,
        },
      },
    );
  },

  getHpUpdateCard(subscriptionId?: string, companyId?: UUID): Promise<HostedPageResponse> {
    return apiRequest<HostedPageResponse>('/ms-license/billing/get-hp-update-card', {
      query: {
        subscription_id: subscriptionId,
        company_id: companyId,
      },
    });
  },

  getPrintInvoicePdfUrl(invoiceId: UUID, companyId?: UUID): string {
    return buildUrl('/ms-license/print-invoice-pdf', {
      invoice_id: invoiceId,
      company_id: companyId,
    });
  },

  getHostedCardUpdateUrl(companyId: UUID, planCode = 'SYNC-STD-MONTHLY'): HostedPageResponse {
    const returnUrl =
      typeof window !== 'undefined' ? `${window.location.origin}/billing?card_updated=1` : '';
    return {
      hosted_page_id: `hp_${companyId.slice(0, 8)}_${Date.now()}`,
      url: `https://billing.sync.smartsheet-procore.com/hosted/card-update?company_id=${encodeURIComponent(
        companyId,
      )}&plan=${encodeURIComponent(planCode)}&return_url=${encodeURIComponent(returnUrl)}`,
      action: 'update_card',
      company_id: companyId,
    };
  },

  // --------------------------------------------------------------------------
  // Admin Billing Settings & Invoice Settlement (`/ms-license/*`)
  // --------------------------------------------------------------------------
  getBillingSettings(companyId?: UUID): Promise<BillingSettings[]> {
    return apiRequest<BillingSettings[]>('/ms-license/billing-settings', {
      query: companyId ? { company_id: companyId } : undefined,
      scope: 'admin',
    });
  },

  updateBillingSettings(body: UpdateBillingSettingsBody): Promise<BillingSettings> {
    return apiRequest<BillingSettings>('/ms-license/update-billing-settings', {
      method: 'POST',
      body,
      scope: 'admin',
    });
  },

  getCompanyInvoicesAdmin(companyId: UUID): Promise<InvoiceRecord[]> {
    return apiRequest<InvoiceRecord[]>('/ms-license/get-company-invoices', {
      query: { company_id: companyId },
      scope: 'admin',
    });
  },

  markInvoiceAsPaid(invoiceId: UUID): Promise<InvoiceRecord> {
    return apiRequest<InvoiceRecord>('/ms-license/mark-invoice-as-paid', {
      method: 'POST',
      body: { invoice_id: invoiceId },
      scope: 'admin',
    });
  },
};
